import { Fragment, useState, useRef, useEffect } from "react";
import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFileCsv } from "@fortawesome/free-solid-svg-icons";
// import { useDownloads } from "../../contexts/DownloadsContext";

import { postVendorListExport } from "../../utils/exportVendorList";

/**
 * Reusable table-level export menu (hamburger dropdown with Export Excel / Export CSV).
 *
 * Props:
 *  - getData        () => { records: [{...}], headers: string[] }
 *                   Callback that returns the current table data at the moment the user clicks export.
 *  - reportTitle    string – title shown on the Excel cover page
 *  - reportSubtitle string – optional subtitle for the cover page
 *  - filePrefix     string – prefix for the downloaded filename (e.g. "RFI_Requests")
 *  - downloadType   string – label used in the Downloads panel (e.g. "RFI Requests")
 *  - buttonClassName string – optional extra classes on the trigger button
 *  - iconClassName   string – optional extra classes on the trigger icon
 */
export default function TableExportMenu({
  getData,
  reportTitle = "Data Export",
  reportSubtitle = "",
  filePrefix = "export",
  downloadType = "Table Export",
  cellStyles = null,
  buttonClassName = "",
  iconClassName = "h-5 w-5",
}) {
  // const { addDownload } = useDownloads();
  const addDownload = () => {}; // Placeholder until DownloadsContext is implemented on this page
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);

  // getData may be async: grids fetch every page of records at export time, so
  // both handlers await it instead of snapshotting the visible page.
  const handleExportCSV = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsExportingCsv(true);
    let records, headers;
    try {
      ({ records, headers } = await getData());
    } catch (err) {
      console.error("CSV export failed:", err);
      alert(`Export failed: ${err.message}`);
      setIsExportingCsv(false);
      return;
    }
    setIsExportingCsv(false);
    if (!records || records.length === 0) return;

    const rows = records.map((record) =>
      headers.map((h) => {
        const val = record[h] ?? "";
        return `"${String(val).replace(/"/g, '""')}"`;
      }),
    );

    const csvContent = [
      headers.map((h) => `"${h}"`).join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fileName = `Diligent_${filePrefix}_${new Date().toISOString().split("T")[0]}.csv`;
    link.download = fileName;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    addDownload({
      id: `csv-${filePrefix}-${Date.now()}`,
      name: reportTitle,
      provider: `${records.length} record${records.length !== 1 ? "s" : ""}`,
      type: downloadType,
      fileType: "csv",
      blob,
      fileName,
    });
  };

  const handleExportExcel = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsExportingExcel(true);
    try {
      const { records, headers } = await getData();
      if (!records || records.length === 0) return;

      const data = await postVendorListExport({
        records,
        headers,
        reportTitle,
        reportSubtitle,
        ...(cellStyles ? { cellStyles } : {}),
      });
      if (!data.success) throw new Error(data.error || "Export failed");

      if (data.excel_url) {
        window.open(data.excel_url, "_blank");
      }

      const fileName =
        data.file_name ||
        `Diligent_${filePrefix}_${new Date().toISOString().split("T")[0]}.xlsx`;
      addDownload({
        id: `excel-${filePrefix}-${Date.now()}`,
        name: reportTitle,
        provider: `${records.length} record${records.length !== 1 ? "s" : ""}`,
        type: downloadType,
        fileType: "excel",
        url: data.excel_url,
        fileName,
      });
    } catch (err) {
      console.error("Excel export failed:", err);
      alert(`Export failed: ${err.message}`);
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button
          className={`-m-1.5 block p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors ${buttonClassName}`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <span className="sr-only">Export options</span>
          <EllipsisVerticalIcon className={iconClassName} aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          portal
          anchor="bottom end"
          className="z-[100] w-52 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none shadow-ring"
        >
          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  className={`${active ? "bg-gray-100 text-gray-900" : "text-gray-700"} group flex w-full items-center px-4 py-2 text-sm ${isExportingCsv ? "opacity-50 cursor-wait" : ""}`}
                  onClick={handleExportCSV}
                  disabled={isExportingCsv}
                >
                  <FontAwesomeIcon
                    icon={faFileCsv}
                    className="mr-3 h-4 w-4 text-green-600"
                  />
                  {isExportingCsv ? "Exporting..." : "Export CSV"}
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  className={`${active ? "bg-gray-100 text-gray-900" : "text-gray-700"} group flex w-full items-center px-4 py-2 text-sm ${isExportingExcel ? "opacity-50 cursor-wait" : ""}`}
                  onClick={handleExportExcel}
                  disabled={isExportingExcel}
                >
                  {isExportingExcel ? (
                    <svg
                      className="animate-spin mr-3 h-4 w-4 text-green-700"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <FontAwesomeIcon
                      icon={faFileExcel}
                      className="mr-3 h-4 w-4 text-green-700"
                    />
                  )}
                  {isExportingExcel ? "Exporting..." : "Export Excel"}
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
